import React, { useState, useEffect } from 'react';

// SERVICES
import { getKamaeScore } from 'services/tileService';

// UTILS
import iconMap from 'utils/iconMap';

// COMPONENTS
import TooltipComponent from '../tooltipComponent';

/**
 * Composant pour afficher les informations spécifiques aux tuiles
 * Affiche le score Kamaé pour la tuile "Kamaé"
 */
const TileSpecificInfo = ({ tileTitle, userEmail, currentBreakpoint = 'lg', className = "" }) => {
  const [kamaeScore, setKamaeScore] = useState(null);
  const [nbCoursSuccess, setNbCoursSuccess] = useState(null);
  const [nbCoursTotal, setNbCoursTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const CeintureKamaeIcon = iconMap['ceintureKamae'];

  // Fonction pour déterminer la couleur de ceinture selon le score
  const getBeltColor = (score) => {
    if (score >= 0 && score <= 4) return '#DFE2E7'; // Blanc
    if (score >= 5 && score <= 15) return '#FFD562'; // Jaune
    if (score >= 16 && score <= 31) return '#FFAD66'; // Orange
    if (score >= 32 && score <= 51) return '#79D56A'; // Vert
    if (score >= 52 && score <= 75) return '#5BC5E7'; // Bleu
    if (score >= 76 && score <= 102) return '#C587FF'; // Violet
    if (score >= 103 && score <= 133) return '#954F39'; // Marron
    if (score >= 134 && score <= 167) return '#000000'; // Noir
    if (score >= 168 && score <= 204) return '#FF4A4A'; // Rouge (estimation)
    if (score >= 205 && score <= 254) return '#B7916C'; // Bronze (estimation)
    if (score >= 255 && score <= 299) return '#ACC1D3'; // Silver (estimation)
    if (score >= 300) return '#E6C270'; // Gold (estimation)
    
    return '#DFE2E7'; // Blanc par défaut
  };

  useEffect(() => {
    // Vérifier si c'est la tuile Kamaé
    if (!tileTitle || !userEmail) return;
    
    const isKamaeTile = tileTitle.toLowerCase().includes('kamaé') || tileTitle.toLowerCase().includes('kamae');
    
    if (!isKamaeTile) return;

    const fetchKamaeScore = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const responseData = await getKamaeScore(userEmail);
        // Les données sont à la racine de la réponse
        const userData = responseData.data['0'] || responseData.data[0]; // Gérer les deux formats possibles
        
        setKamaeScore(userData.ku_score);
        // nb_cours_succes et nb_cours_total sont à la racine de la réponse, pas dans data
        setNbCoursSuccess(responseData.data.nb_cours_succes);
        setNbCoursTotal(responseData.data.nb_cours_total);
      } catch (err) {
        console.error('Erreur lors de la récupération du score Kamaé:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchKamaeScore();
  }, [tileTitle, userEmail]);

  // Ne rien afficher si ce n'est pas la tuile Kamaé
  const isKamaeTile = tileTitle?.toLowerCase().includes('kamaé') || tileTitle?.toLowerCase().includes('kamae');
  if (!isKamaeTile) {
    return null;
  }

  // Mode compact : afficher uniquement l'icône avec tooltip en breakpoint xs
  if (currentBreakpoint === 'xs') {
    if (loading || !kamaeScore) {
      return (
        <div className={className}>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blanc"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={`text-xs text-red-300 ${className}`}>
          Erreur
        </div>
      );
    }

    // Contenu du tooltip avec les détails
    const tooltipContent = nbCoursSuccess !== null && nbCoursTotal !== null 
      ? `${kamaeScore} pts\n${nbCoursSuccess}/${nbCoursTotal} cours` 
      : `${kamaeScore} pts`;
    return (
      <div className={className}>
        <TooltipComponent title={tooltipContent} placement="left">
          <CeintureKamaeIcon 
            className="size-5" 
            style={{ fill: getBeltColor(kamaeScore) }}
          />
        </TooltipComponent>
      </div>
    );
  }

  // Mode détaillé : afficher le texte en mode xs
  if (loading || !kamaeScore) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="animate-spin rounded-full size-3 border-b-2 border-blanc"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-xs text-red-300 ${className}`}>
        Erreur
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-0.5 bg-noir-50 rounded-lg p-1 ${className}`}>
      {/* Score avec icône ceinture */}
      <div className="flex items-center gap-0.5">
        <span className="text-sm text-blanc font-bold">
          {kamaeScore} pts
        </span>
        <CeintureKamaeIcon 
          className="size-5" 
          style={{ fill: getBeltColor(kamaeScore) }}
        />
      </div>
      {/* Nombre de cours */}
      {nbCoursSuccess !== null && nbCoursTotal !== null && (
        <span className="text-xs text-blanc opacity-75">
          {nbCoursSuccess}/{nbCoursTotal} cours
        </span>
      )}
    </div>
  );
};

export default TileSpecificInfo;
